import { useState } from 'react';
import ImageUploading from 'react-images-uploading';
import { v4 as uuidv4 } from 'uuid';

import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import FormError from '@/shared-components/error/components/FormError';
import Heading from '@/shared-components/text/components/Heading';

import PopupUpdateSecondaryPicture from './PopupUpdateSecondaryPicture';

import './styles/UploadSecondaryPictures.css';

export default function UploadSecondaryPictures({ internal, secondaryPictures, setSecondaryPictures, formError, setFormError, disabled=false, }) {
  const t = useTranslations('common.forms.add_artwork');
  const [pendingCropFiles, setPendingCropFiles] = useState([]);
  const activeFileToCrop = pendingCropFiles[0] || null;

  function queueSecondaryPicturesForCrop(imageList) {
    if (disabled) {
      return;
    }

    const selectedFiles = imageList
      .map(imageData => imageData.file)
      .filter(Boolean);

    if (selectedFiles.length === 0) {
      return;
    }

    if ((secondaryPictures.length + selectedFiles.length) > 4) {
      setFormError({
        element: "artwork_secondary_pictures_error", 
        error_code: "TOO_MANY_ARTWORK_IMAGES"
      });
      return;
    }

    setFormError(null);
    setPendingCropFiles(selectedFiles);
  }

  function uploadSecondaryPicture(imageFile) {
    const newImageId = uuidv4();

    setSecondaryPictures(prevValue => { 
      return [
        ...prevValue, 
        {
          loading: true,
          progress: 0,
          image_id: newImageId,
          image_original_data: {
            name: imageFile.name
          }
        }
      ];
    });

    UserService.uploadImage(imageFile, (progress) => {
      setSecondaryPictures(prevValue => prevValue.map(picture => {
        if(picture.image_id === newImageId) {
          return {
            ...picture,
            progress
          };
        } else {
          return picture;
        }
      }));
    }).then(({ response, data }) => {
      if(response.ok) {
        setSecondaryPictures(prevValue => prevValue.map(picture => {
          if(picture.image_id === newImageId) {
            return data;
          } else {
            return picture;
          }
        }));
      } else {
        setSecondaryPictures(prevValue => prevValue.filter(picture => picture.image_id !== newImageId));
        alert(t('media.upload_error'));
      }
    });
  }

  function handleCropExit() {
    setPendingCropFiles([]);
  }

  function handleSecondaryPictureCropComplete(croppedFile) {
    if (!activeFileToCrop || !croppedFile) {
      handleCropExit();
      return;
    }

    const croppedFileWithOriginalName = new File(
      [croppedFile],
      activeFileToCrop.name,
      { type: croppedFile.type || activeFileToCrop.type || 'image/png' }
    );

    uploadSecondaryPicture(croppedFileWithOriginalName);
    setPendingCropFiles(prevValue => prevValue.slice(1));
  }

  function handleSecondaryPicturesError(errors) {
    if(errors.maxNumber) setFormError({ element: "artwork_secondary_pictures_error", error_code: "TOO_MANY_ARTWORK_IMAGES" });
    else if(errors.acceptType) setFormError({ element: "artwork_secondary_pictures_error", error_code: "INVALID_FILE_FORMAT" });
    else if(errors.maxFileSize) setFormError({ element: "artwork_secondary_pictures_error", error_code: "ARTWORK_FILE_EXCEEDS_SIZE" });
  }
    
  return (
    <>
      <ImageUploading 
        maxNumber={4} 
        multiple 
        onChange={queueSecondaryPicturesForCrop} 
        onError={handleSecondaryPicturesError} 
        maxFileSize={10000000}
      >
        {({ onImageUpload, isDragging, dragProps }) => (
          <div className={`add-artwork-media-form__add-secondary-pictures-button-outter ${internal ? "internal" : ""}`}>
            <div
              id="artwork_secondary_pictures_error"
              {...dragProps}
              onClick={() => {
                if (!disabled) onImageUpload();
              }}
              className={[
                "add-artwork-media-form__add-secondary-pictures-button",
                "element-non-selectable",
                isDragging && "dragging",
                disabled && "is-disabled",        // <--- aquí
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <Heading className="add-artwork-media-form__upload-button-text">
                {t('media.add_secondary_picture')}
              </Heading>
            </div>

            <FormError error={formError} element="artwork_secondary_pictures_error"/>
          </div>
        )}
      </ImageUploading>

      <PopupUpdateSecondaryPicture
        open={Boolean(activeFileToCrop)}
        image={activeFileToCrop}
        exitCrop={handleCropExit}
        setCroppedFile={handleSecondaryPictureCropComplete}
      />
    </>
  );
}
